import { useState } from 'react';
import { SEO } from '../../components/seo/SEO';
import { getBaseUrl } from '../../lib/utils';

const inquiryTypes = [
  { icon: 'ri-feedback-line', label: '記事へのフィードバック・情報提供' },
  { icon: 'ri-links-line', label: 'コラボレーション・提携のご提案' },
  { icon: 'ri-error-warning-line', label: '誤情報・訂正のご指摘' },
  { icon: 'ri-questionnaire-line', label: 'その他ご意見・ご要望' },
];

const faqItems = [
  {
    q: '記事の無断転載はできますか？',
    a: '引用は出典明記の上で可能です。記事全文の転載や商用二次利用はご遠慮ください。',
  },
  {
    q: '記事の内容に誤りを見つけた場合は？',
    a: 'お問い合わせフォームからご連絡ください。正確な情報の提供に努めています。',
  },
  {
    q: '記事のリクエストはできますか？',
    a: 'フォームからリクエストいただけます。実際に制作中の内容に関連するリクエストほど記事化しやすいです。',
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const baseUrl = getBaseUrl();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ContactPage',
        name: 'お問い合わせ — つくってなんぼ',
        url: `${baseUrl}/contact`,
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d74mctmcsbv12mkud7n0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="お問い合わせ — つくってなんぼ | AI活用・個人開発・業務効率化 実践メディア"
        description="つくってなんぼへのお問い合わせはこちら。記事へのフィードバック・コラボレーション・誤情報の訂正などお気軽にどうぞ。"
        canonical="/contact"
        jsonLd={jsonLd}
      />

      <main className="min-h-screen bg-[#F6F1E8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
          {/* Header */}
          <div className="mb-10 max-w-2xl">
            <span className="text-xs font-bold tracking-widest uppercase text-[#C96F3B]">お問い合わせ</span>
            <div className="w-8 h-0.5 bg-[#C96F3B] mt-1.5 mb-4"></div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3E332A] mb-3"
              style={{ fontFamily: "'M PLUS 1p', 'Noto Sans JP', sans-serif" }}>
              気軽に声をかけてください
            </h1>
            <p className="text-base text-[#7A6A5A] leading-relaxed">
              記事へのフィードバック、コラボレーション、誤情報の指摘など、どんな内容でも歓迎です。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-7">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="rounded-lg border border-[#C7B8A3] bg-[#FFFDF8] p-5 sm:p-7"
                style={{ boxShadow: '0 1px 4px rgba(62,51,42,0.06)' }}>
                <h2 className="text-sm font-bold text-[#3E332A] mb-5 flex items-center gap-2">
                  <i className="ri-send-plane-line text-[#C96F3B]"></i>
                  メッセージを送る
                </h2>

                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-bold text-[#3E332A] mb-1.5">
                        お名前 <span className="text-[#C96F3B]">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="山田 太郎"
                        className="w-full px-3 py-2.5 bg-[#F6F1E8] border border-[#C7B8A3] rounded text-sm text-[#3E332A] placeholder-[#B0A090] focus:outline-none focus:border-[#C96F3B] focus:ring-1 focus:ring-[#C96F3B] transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-bold text-[#3E332A] mb-1.5">
                        メールアドレス <span className="text-[#C96F3B]">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder="example@email.com"
                        className="w-full px-3 py-2.5 bg-[#F6F1E8] border border-[#C7B8A3] rounded text-sm text-[#3E332A] placeholder-[#B0A090] focus:outline-none focus:border-[#C96F3B] focus:ring-1 focus:ring-[#C96F3B] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-xs font-bold text-[#3E332A] mb-1.5">
                      件名 <span className="text-[#C96F3B]">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      placeholder="記事フィードバック / コラボレーション相談 など"
                      className="w-full px-3 py-2.5 bg-[#F6F1E8] border border-[#C7B8A3] rounded text-sm text-[#3E332A] placeholder-[#B0A090] focus:outline-none focus:border-[#C96F3B] focus:ring-1 focus:ring-[#C96F3B] transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-bold text-[#3E332A] mb-1.5">
                      メッセージ <span className="text-[#C96F3B]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="お気軽にどうぞ。具体的な内容があるとスムーズに対応できます。"
                      className="w-full px-3 py-2.5 bg-[#F6F1E8] border border-[#C7B8A3] rounded text-sm text-[#3E332A] placeholder-[#B0A090] focus:outline-none focus:border-[#C96F3B] focus:ring-1 focus:ring-[#C96F3B] resize-none transition-colors"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 450 ? 'text-[#C96F3B]' : 'text-[#7A6A5A]'}`}>
                      {charCount}/500文字
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#C96F3B] text-white rounded text-sm font-bold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'M PLUS 1p', 'Noto Sans JP', sans-serif" }}
                  >
                    <i className="ri-send-plane-fill"></i>
                    送信する
                  </button>

                  {status === 'success' && (
                    <div className="p-3 bg-[#EFF5F2] border border-[#A8C4B8] rounded text-[#5E7C6B] text-sm flex items-center gap-2">
                      <i className="ri-check-circle-line text-base flex-shrink-0"></i>
                      送信が完了しました。2〜3営業日以内にご返信します。
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="p-3 bg-[#FEF3E8] border border-[#E8C4A0] rounded text-[#C96F3B] text-sm flex items-center gap-2">
                      <i className="ri-error-warning-line text-base flex-shrink-0"></i>
                      送信に失敗しました。しばらく経ってから再度お試しください。
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-4">
              {/* Inquiry types */}
              <div className="rounded-lg border border-[#C7B8A3] bg-[#FFFDF8] p-4 sm:p-5"
                style={{ boxShadow: '0 1px 4px rgba(62,51,42,0.06)' }}>
                <h3 className="text-xs font-bold text-[#3E332A] mb-3 flex items-center gap-2">
                  <i className="ri-list-check-2 text-[#C96F3B]"></i>
                  お問い合わせの種類
                </h3>
                <ul className="space-y-2.5">
                  {inquiryTypes.map((t) => (
                    <li key={t.label} className="flex items-start gap-2 text-xs text-[#7A6A5A]">
                      <i className={`${t.icon} text-[#C96F3B] text-sm flex-shrink-0 mt-0.5`}></i>
                      {t.label}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Response time */}
              <div className="rounded-lg border border-[#C7B8A3] bg-[#FFFDF8] p-4 sm:p-5"
                style={{ boxShadow: '0 1px 4px rgba(62,51,42,0.06)' }}>
                <h3 className="text-xs font-bold text-[#3E332A] mb-3 flex items-center gap-2">
                  <i className="ri-time-line text-[#C96F3B]"></i>
                  返信について
                </h3>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-[#EFF5F2] flex-shrink-0 mt-0.5">
                      <i className="ri-calendar-check-line text-[#5E7C6B] text-xs"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#3E332A]">通常2〜3営業日以内</p>
                      <p className="text-xs text-[#7A6A5A] mt-0.5">土日祝を除く平日対応です。</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded bg-[#FEF3E8] flex-shrink-0 mt-0.5">
                      <i className="ri-mail-check-line text-[#C96F3B] text-xs"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#3E332A]">迷惑メールフォルダを確認</p>
                      <p className="text-xs text-[#7A6A5A] mt-0.5">返信がない場合は迷惑フォルダを確認してください。</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div className="rounded-lg border border-[#C7B8A3] bg-[#FFFDF8] p-4 sm:p-5"
                style={{ boxShadow: '0 1px 4px rgba(62,51,42,0.06)' }}>
                <h3 className="text-xs font-bold text-[#3E332A] mb-3 flex items-center gap-2">
                  <i className="ri-question-answer-line text-[#C96F3B]"></i>
                  よくある質問
                </h3>
                <div className="space-y-3">
                  {faqItems.map((item, i) => (
                    <div key={i} className={i < faqItems.length - 1 ? 'border-b border-[#E8DCC8] pb-3' : ''}>
                      <p className="text-xs font-bold text-[#3E332A] mb-1">Q. {item.q}</p>
                      <p className="text-xs text-[#7A6A5A] leading-relaxed">A. {item.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
